import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import clsx from "clsx";
import { BookCover } from "./BookCover";

export const BookChapter = ({ book, flip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const numY = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id={`chapter-${book.number}`}
      ref={ref}
      data-testid={`chapter-${book.id}`}
      className="relative flex min-h-screen items-center overflow-hidden py-28"
    >
      <motion.img
        src={book.image}
        alt=""
        aria-hidden="true"
        style={{ y: imgY }}
        className="absolute inset-0 h-[116%] w-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-[#0C0A09]/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09] via-transparent to-[#0C0A09]" />

      <motion.span
        style={{ y: numY }}
        aria-hidden="true"
        className={clsx(
          "text-outline pointer-events-none absolute top-1/2 -translate-y-1/2 font-serif text-[42vw] font-black leading-none md:text-[30vw]",
          flip ? "-right-[4vw]" : "-left-[4vw]"
        )}
      >
        {book.number}
      </motion.span>

      <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 90, rotate: flip ? 6 : -6 }}
          whileInView={{ opacity: 1, y: 0, rotate: flip ? 3 : -3 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className={clsx("mx-auto w-64 md:w-80 lg:w-[24rem]", flip && "lg:order-2")}
        >
          <BookCover book={book} />
        </motion.div>

        <div className={clsx(flip && "lg:order-1 lg:text-right")}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className={clsx("mb-6 flex items-center gap-4", flip && "lg:justify-end")}>
              <span className="h-px w-10" style={{ backgroundColor: book.accent }} />
              <p className="font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-[#A8A29E]">
                Chapter {book.number} — {book.genre}
              </p>
            </div>
            <h2 className="font-serif text-4xl font-bold leading-[1.02] tracking-tight md:text-6xl">
              {book.title}
            </h2>
            <p className="mt-5 font-serif text-lg italic md:text-xl" style={{ color: book.accent }}>
              {book.pull}
            </p>
            <p className="mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-[#C9C5BC] md:text-lg lg:max-w-none">
              {book.description}
            </p>
            <div className={clsx("mt-10 flex flex-wrap gap-4", flip && "lg:justify-end")}>
              <a
                href={book.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`buy-${book.id}-button`}
                className="group flex items-center gap-3 bg-[#F4F4F0] px-6 py-3.5 font-sans text-[11px] font-bold uppercase tracking-[0.25em] text-[#0C0A09] transition-colors duration-300 hover:bg-[#E63946] hover:text-white"
              >
                Buy on Amazon.ca
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#ad-studio"
                data-testid={`ad-${book.id}-button`}
                className="flex items-center gap-2 border border-white/20 px-6 py-3.5 font-sans text-[11px] font-bold uppercase tracking-[0.25em] text-[#F4F4F0] transition-colors duration-300 hover:border-white/60"
              >
                <Sparkles className="h-4 w-4 text-[#E63946]" />
                Ad for this book
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
